import React, { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { formatPhone } from '../../../utils/inputMasks';

interface CreateAccountViewProps {
  onSubmit: (data: { name: string; email: string; phone: string; password: string; turnstileToken: string }) => Promise<void>;
  onSwitchToLogin: () => void;
  isLoading: boolean;
}

const CreateAccountView: React.FC<CreateAccountViewProps> = ({
  onSubmit,
  onSwitchToLogin,
  isLoading
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [validationError, setValidationError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');

  const validateForm = () => {
    if (!name.trim()) {
      setValidationError('Nome é obrigatório');
      return false;
    }
    if (!email.trim()) {
      setValidationError('Email é obrigatório');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setValidationError('Email inválido');
      return false;
    }
    if (!phone.trim()) {
      setValidationError('Telefone é obrigatório');
      return false;
    }
    if (phone.replace(/\D/g, '').length < 10) {
      setValidationError('Telefone deve ter pelo menos 10 dígitos');
      return false;
    }
    if (!password.trim()) {
      setValidationError('Senha é obrigatória');
      return false;
    }
    if (password.length < 6) {
      setValidationError('Senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (!turnstileToken) {
      setValidationError('Por favor, complete a verificação de segurança');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    
    if (!validateForm()) {
      return;
    }
    
    await onSubmit({ name, email, phone, password, turnstileToken });
  };

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    setValidationError(''); // Clear any Turnstile-related errors
  };

  const handleTurnstileError = () => {
    setTurnstileToken('');
    setValidationError('Erro na verificação de segurança. Tente novamente.');
  };

  return (
    <>
      <div className="text-sm text-gray-600 mb-2">
        Crie sua conta na DIO para continuar
      </div>

      {validationError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
          {validationError}
        </div>
      )}

      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="name" className="block text-xs font-semibold text-gray-700 mb-1">Nome completo</label>
          <div className="relative">
            <input
              id="name"
              type="text"
              placeholder="Seu nome completo"
              className="input pr-10"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
            {name && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-xs font-semibold text-gray-700 mb-1">E-mail</label>
          <div className="relative">
            <input
              id="email"
              type="email"
              placeholder="seu@email.com"
              className="input pr-10"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
            {email && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs font-semibold text-gray-700 mb-1">Celular com DDD</label>
          <div className="relative">
            <input
              id="phone"
              type="tel"
              placeholder="(99) 99999-9999"
              className="input pr-10"
              value={phone}
              onChange={e => setPhone(formatPhone(e.target.value))}
              required
            />
            {phone && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="create-password" className="block text-xs font-semibold text-gray-700 mb-1">Crie uma senha</label>
          <div className="relative">
            <input
              id="create-password"
              type="password"
              placeholder="********"
              className="input pr-10"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            {password && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </span>
            )}
          </div>
        </div>

        {/* Turnstile Security Verification */}
        <div className="flex flex-col gap-2">
          <label className="block text-xs font-semibold text-gray-700">Verificação de Segurança</label>
          <div className="flex justify-center">
            <Turnstile
              siteKey={process.env["APP_TURNSTILE_SITE_KEY"] || ''}
              onSuccess={handleTurnstileSuccess}
              onError={handleTurnstileError}
              options={{
                theme: 'light',
                size: 'normal',
                language: 'pt-BR'
              }}
            />
          </div>
        </div>

        {/* Display validation error if exists */}
        {validationError && (
          <div className="text-red-600 text-sm bg-red-50 p-2 rounded border border-red-200">
            {validationError}
          </div>
        )}

        <button
          type="submit"
          className="mt-2 w-full btn btn-primary"
          aria-label="Continuar"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSubmit(e);
            }
          }}
          disabled={isLoading}
        >
          {isLoading ? 'Processando...' : 'Continuar'}
        </button>

        <div className="text-center text-sm text-gray-600 mt-4">
          Já tem conta?{' '}
          <button 
            type="button" 
            onClick={onSwitchToLogin} 
            className="text-primary font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50" 
            aria-label="Fazer login" 
            tabIndex={0} 
            onKeyDown={(e) => { 
              if (e.key === 'Enter' || e.key === ' ') { 
                onSwitchToLogin(); 
              } 
            }}
          >
            Faça login
          </button>
        </div>
      </form>
    </>
  );
};

export default CreateAccountView;
